import * as dialogs from "@client/features/dialogs";
import {
    useWorkoutData,
    annotateWithExercises,
    type useWorkout,
} from "@client/features/workouts";
import * as workouts from "@client/features/workouts";
import * as forms from "@client/forms";
import {useAsync} from "@client/hooks";
import * as analytics from "@client/shared/analytics";
import {server} from "@reactivated";
import React from "react";

export function SectionDialog(props: {
    workout_uuid: UUID;
    close: (output: unknown) => void;
    setSections: ReturnType<typeof useWorkout>["setSections"];
}) {
    const [sectionValues, setSectionValues] = React.useState({
        type: "BASIC" as "BASIC" | "ROUNDS" | "DURATION",
        name: "",
        duration: null as number | null,
        rounds: null as number | null,
    });

    const result = useAsync(async () => {
        // circular imports right now
        return server.journal.workouts.recent_sections({
            offset: 0,
            exclude_workout: props.workout_uuid,
        });
    }, []);

    const [query, setQuery] = React.useState("");

    const queryForm = forms.useField(
        {kind: "text", name: "query", placeholder: "Filter..."},
        {value: query, onChange: setQuery},
    );

    const [route, setRoute] = React.useState<"create" | "recent">("create");

    const selectSection = (section: server.journal.workouts.WorkoutSection.output) => {
        void analytics.track("REUSED_SECTION");

        props.setSections((sections) => [
            ...sections,
            {
                ...section,
                copied_from: {uuid: section.uuid},
                uuid: crypto.randomUUID(),
                groups: section.groups.map((group) => ({
                    ...group,
                    uuid: crypto.randomUUID(),
                    exercises: group.exercises.map((exercise) => ({
                        ...exercise,
                        uuid: crypto.randomUUID(),
                        rest_duration: null,
                        sets: workouts.copySets(exercise.sets),
                    })),
                })),
            },
        ]);
        props.close(null);
    };
    const sectionForm = forms.useForm(
        {
            fields: {
                type: {
                    kind: "select",
                    label: "Type",
                    nullable: false,
                    options: [
                        ["BASIC", "Basic"],
                        ["ROUNDS", "Rounds"],
                        ["DURATION", "Duration"],
                    ],
                    required: true,
                },
                name: {
                    kind: "text",
                    label: "Name",
                },
            },
        },
        {
            value: {name: sectionValues.name, type: sectionValues.type},
            onChange: (values) =>
                setSectionValues((previous) => ({...previous, ...values})),
        },
    );

    if (route == "recent") {
        return (
            <dialogs.HModal.Content
                title={
                    <div className="space-y-2">
                        <h2 className="text-lg font-semibold">Sections</h2>
                        <forms.Widget field={queryForm} />
                    </div>
                }
                content={
                    <div className="space-y-4">
                        {result.value?.type != "success" ? (
                            <div>Loading...</div>
                        ) : (
                            <div className="flex flex-col divide-y divide-gray-200">
                                {result.value.data
                                    .map(annotateWithExercises)
                                    .filter((section) =>
                                        query == ""
                                            ? true
                                            : `${section.name} ${section.workout.name} ${section.workout.group?.name ?? ""} ${section.workout.group?.program.name ?? ""}`
                                                  .toLowerCase()
                                                  .includes(query.toLowerCase()),
                                    )
                                    .map((questionType) => (
                                        <div
                                            key={questionType.uuid}
                                            className="flex items-center gap-2 px-2 py-3"
                                        >
                                            <div className="flex-1">
                                                <h2 className="text-sm font-semibold sm:text-[15px]">
                                                    {questionType.name}

                                                    <span>
                                                        {" "}
                                                        —{" "}
                                                        {questionType.workout.name}{" "}
                                                    </span>
                                                    {questionType.workout.group !=
                                                        null && (
                                                        <>
                                                            <span>
                                                                {" "}
                                                                —{" "}
                                                                {
                                                                    questionType.workout
                                                                        .group.name
                                                                }{" "}
                                                            </span>
                                                            <span>
                                                                {" "}
                                                                —{" "}
                                                                {
                                                                    questionType.workout
                                                                        .group.program
                                                                        .name
                                                                }{" "}
                                                            </span>
                                                        </>
                                                    )}
                                                </h2>
                                                <div>
                                                    <span>
                                                        {questionType.exercises
                                                            .length == 1
                                                            ? "1 exercise"
                                                            : `${questionType.exercises.length} exercises`}
                                                    </span>
                                                </div>
                                            </div>
                                            <div>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        selectSection(questionType);
                                                    }}
                                                    className="rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 focus-visible:outline-solid disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500 disabled:shadow-none"
                                                >
                                                    Select
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                {result.value.data.length == 0 && (
                                    <p>No recent sections found.</p>
                                )}
                            </div>
                        )}
                    </div>
                }
                actions={
                    <button
                        type="button"
                        className="w-full rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 focus-visible:outline-solid disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500 disabled:shadow-none"
                        onClick={() => {
                            setRoute("create");
                        }}
                    >
                        Create new section
                    </button>
                }
            />
        );
    }

    return (
        <dialogs.HModal.Content
            title={<h2 className="text-lg font-semibold">Create section</h2>}
            content={
                <div className="space-y-4">
                    <forms.Field field={sectionForm.fields.name} />
                    <forms.Field field={sectionForm.fields.type} />
                    <ul className="space-y-4">
                        <li>
                            <strong>Basic</strong> sections just contain exercises and
                            supersets
                        </li>
                        <li>
                            <strong>Rounds</strong> prescribe a set number of rounds and
                            collect time
                        </li>
                        <li>
                            <strong>Duration</strong> prescribes a total time and
                            collects number of rounds
                        </li>
                    </ul>

                    <button
                        type="button"
                        className="w-full rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 focus-visible:outline-solid disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500 disabled:shadow-none"
                        onClick={() => {
                            setRoute("recent");
                        }}
                    >
                        Recent sections
                    </button>
                    {/*
                    {sectionValues.type == "DURATION" && (
                        <forms.Field field={sectionForm.fields.rounds} />
                    )}
                    {sectionValues.type == "ROUNDS" && (
                        <forms.Field field={sectionForm.fields.duration} />
                    )}
                    */}
                </div>
            }
            actions={
                <button
                    type="button"
                    className="w-full rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 focus-visible:outline-solid disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500 disabled:shadow-none"
                    disabled={sectionForm.values.name == ""}
                    onClick={() => {
                        void analytics.track("CREATED_SECTION", {
                            section_type: sectionForm.values.type,
                        });
                        props.setSections((sections) => [
                            ...sections,
                            {
                                ...sectionForm.values,
                                instructions: "",
                                uuid: crypto.randomUUID(),
                                copied_from: null,
                                duration: null,
                                rounds: null,
                                groups: [],
                            },
                        ]);
                        props.close(null);
                    }}
                >
                    Create
                </button>
            }
        />
    );
}

export function TemplateDialog(props: {
    close: (output: server.journal.workouts.WorkoutTemplate.output) => void;
}) {
    const workoutData = useWorkoutData();

    if (workoutData == null) {
        return <div>Loading ...</div>;
    }
    const {templates} = workoutData;

    const content = (
        <>
            <ul
                role="list"
                className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
            >
                {templates.map(annotateWithExercises).map((template) => (
                    <li
                        key={template.id}
                        className="relative col-span-1 rounded-lg border border-gray-200 bg-white shadow-xs"
                    >
                        <button
                            className="absolute inset-0"
                            onClick={() => {
                                props.close(template);
                            }}
                        />
                        <div className="flex w-full items-center justify-between space-x-6 p-6">
                            <div className="flex-1 truncate">
                                <div className="flex items-center space-x-3">
                                    <h3 className="truncate text-sm font-medium text-gray-900">
                                        {template.name}
                                    </h3>
                                </div>
                                <p className="mt-1 truncate text-sm text-gray-500">
                                    {template.exercises.length == 1
                                        ? "1 exercise"
                                        : `${template.exercises.length} exercises`}

                                    {template.user_id == null && (
                                        <span className="ml-2 inline-flex shrink-0 items-center rounded-full bg-green-50 px-1.5 py-0.5 text-xs font-medium text-green-700 ring-1 ring-green-600/20 ring-inset">
                                            Sample
                                        </span>
                                    )}
                                </p>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        </>
    );

    return (
        <dialogs.HModal.Content
            title={<h2 className="text-lg font-semibold">Pick template</h2>}
            content={<div className="space-y-4">{content}</div>}
            actions={null}
        />
    );
}
